/**
 * v101.181.0 · Desfase con Odoo (módulo Órdenes de trabajo) · solo lectura.
 * Cerradas / canceladas en AlfaField cuya tarea sigue ABIERTA en Odoo (etapa leída en vivo).
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import api from "../lib/api";
import { toast } from "sonner";
import { Loader2, Download, RefreshCw, CheckCircle2, XCircle, ExternalLink } from "lucide-react";

const TABS = [
  { key: "cierre", label: "Cerradas aquí · abiertas en Odoo", icon: CheckCircle2, color: "#059669" },
  { key: "cancel", label: "Canceladas aquí · abiertas en Odoo", icon: XCircle, color: "#DC2626" },
];

const ENVIO_COLOR = {
  completado: "bg-amber-100 text-amber-800",
  pendiente_sync: "bg-sky-100 text-sky-800",
  error_negocio: "bg-red-100 text-red-800",
  error_validacion: "bg-red-100 text-red-800",
  "": "bg-slate-100 text-slate-700",
};

const hace90 = () => {
  const d = new Date(Date.now() - 90 * 86400000);
  return d.toISOString().slice(0, 10);
};

export default function OdooDesfasePanel({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("cierre");
  const [desde, setDesde] = useState(hace90());
  const [hasta, setHasta] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyXls, setBusyXls] = useState(false);

  const params = useCallback(() => ({ tipo: "todos", desde: desde || undefined, hasta: hasta || undefined }), [desde, hasta]);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/v1/odoo-desfase", { params: params(), timeout: 180000 });
      setData(d);
    } catch (e) {
      toast.error(`No se pudo consultar el desfase con Odoo: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { if (open && !data) cargar(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const descargar = async () => {
    setBusyXls(true);
    try {
      const resp = await api.get("/v1/odoo-desfase/export.xlsx", { params: params(), responseType: "blob", timeout: 300000 });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `desfase_odoo_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("No se pudo descargar el Excel del desfase con Odoo");
    } finally {
      setBusyXls(false);
    }
  };

  const items = (data?.items || []).filter((i) => i.tipo === tab);
  const res = data?.resumen || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[96vw] max-h-[90vh] overflow-hidden flex flex-col bg-white" data-testid="odoo-desfase-panel">
        <DialogHeader>
          <DialogTitle className="text-[#0A3747]">Desfase con Odoo</DialogTitle>
          <DialogDescription className="text-xs">
            OTs cerradas o canceladas en AlfaField cuya tarea sigue abierta en Odoo. La etapa se consulta en vivo en Odoo.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-2">
          <label className="text-[11px] text-slate-600">Desde
            <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
              className="block mt-0.5 px-2 py-1 rounded-lg border border-slate-200 text-xs" data-testid="desfase-desde" />
          </label>
          <label className="text-[11px] text-slate-600">Hasta
            <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)}
              className="block mt-0.5 px-2 py-1 rounded-lg border border-slate-200 text-xs" data-testid="desfase-hasta" />
          </label>
          <button onClick={cargar} disabled={loading} className="neu-btn neu-btn-primary" style={{ padding: "6px 10px", fontSize: 11 }} data-testid="desfase-consultar">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Consultar
          </button>
          <button onClick={descargar} disabled={busyXls || loading} className="neu-btn" style={{ padding: "6px 10px", fontSize: 11 }} data-testid="desfase-excel">
            {busyXls ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Excel
          </button>
          {data && !data.fuente_odoo && (
            <span className="text-[11px] text-red-700 bg-red-50 rounded-lg px-2 py-1">
              Sin conexión a Odoo: no se pudo leer la etapa de las tareas.
            </span>
          )}
        </div>

        <div className="flex gap-2 flex-wrap">
          {TABS.map((t) => {
            const Ic = t.icon;
            const r = res[t.key] || {};
            const active = tab === t.key;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`desfase-tab-${t.key}`}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-semibold ${active ? "border-[#0A3747] bg-[#0A3747] text-white" : "border-slate-200 bg-white text-slate-700"}`}>
                <Ic size={14} color={active ? "#fff" : t.color} />
                {t.label}
                <span className={`px-1.5 rounded-full text-[11px] ${active ? "bg-white/20" : "bg-slate-100"}`}>
                  {r.desfase ?? "–"} de {r.total ?? "–"}
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-auto border border-slate-100 rounded-xl">
          {loading && !data ? (
            <div className="p-8 text-center text-xs text-slate-500"><Loader2 size={16} className="animate-spin inline mr-2" />Consultando Odoo…</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-xs text-slate-500">
              {data ? "Sin desfase: todo lo de este periodo también está cerrado/cancelado en Odoo." : ""}
            </div>
          ) : (
            <table className="w-full text-[11px]">
              <thead className="sticky top-0 bg-slate-50 text-slate-600">
                <tr>
                  {["OT", "Fecha", "Cliente", "Sucursal", "Técnico", "Etapa en Odoo", "Envío a Odoo", "Respuesta de Odoo", ""].map((h) => (
                    <th key={h} className="text-left font-semibold px-2 py-1.5 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={`${it.tipo}-${it.odoo_task_id}`} className="border-t border-slate-100 align-top">
                    <td className="px-2 py-1.5 font-mono font-semibold text-[#0A3747] whitespace-nowrap">{it.ot}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      {it.fecha ? new Date(it.fecha).toLocaleString("es-EC", { timeZone: "America/Guayaquil", dateStyle: "short", timeStyle: "short" }) : "—"}
                    </td>
                    <td className="px-2 py-1.5 max-w-[180px] truncate" title={it.cliente}>{it.cliente || "—"}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap">{it.sucursal || "—"}</td>
                    <td className="px-2 py-1.5 max-w-[140px] truncate" title={it.tecnico}>{it.tecnico || "—"}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap">{it.etapa_odoo || "—"}</td>
                    <td className="px-2 py-1.5">
                      <span className={`inline-block px-1.5 py-0.5 rounded-md ${ENVIO_COLOR[it.envio_estado] || ENVIO_COLOR[""]}`}>{it.envio}</span>
                    </td>
                    <td className="px-2 py-1.5 max-w-[260px] text-slate-600" title={it.respuesta_odoo}>
                      <div className="line-clamp-2">{it.respuesta_odoo || "—"}</div>
                    </td>
                    <td className="px-2 py-1.5">
                      <button onClick={() => { onOpenChange(false); navigate(`/ot-smart/${it.odoo_task_id}`); }}
                        className="text-[#0A3747] hover:underline flex items-center gap-1 whitespace-nowrap" title="Abrir la OT">
                        <ExternalLink size={12} /> Abrir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
